import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo, { buildBreadcrumbSchema, buildFaqSchema, buildLocalBusinessSchema } from "../components/Seo";
import { api } from "../lib/api";
import { LeadForm, RichBody } from "./WeddingsLanding";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Local SEO city x topic page. Mounted at /locations/:city/:topic.
 * Example: /locations/los-angeles/wedding-venues
 */
export default function LocationPage() {
  const { city, topic } = useParams();
  const [data, setData] = useState(null);
  const [rich, setRich] = useState(null);
  const preview = new URLSearchParams(window.location.search).get("preview");

  useEffect(() => {
    setRich(null);
    (preview ? api.get(`/seo-center/pages/${preview}/preview`) : axios.get(`${API}/public/seo-legacy/${city}/${topic}`))
      .then((r) => { setRich(r.data); if (!preview) axios.post(`${API}/public/weddings-view?page_id=${r.data.page_id}`).catch(() => {}); })
      .catch(() => setRich(null));
  }, [city, topic, preview]);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/seo/locations/${city}/${topic}`)
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setData(false));
    return () => {
      alive = false;
    };
  }, [city, topic]);

  if (data === false) {
    return (
      <div className="min-h-screen bg-[var(--bg)]">
        <Navbar />
        <div className="container-wide section-pad text-center">
          <h1 className="font-serif text-4xl">Location not found</h1>
          <Link to="/locations" className="btn-gold mt-6 inline-block">
            Browse cities
          </Link>
        </div>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--ink)]">
      <Seo
        title={rich?.title || data.h1}
        description={rich?.meta || data.tag}
        path={`/locations/${city}/${topic}`}
        noindex={!!data.noindex || !!preview}
        keywords={data.keywords}
        schema={[
          buildBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Locations", url: "/locations" },
            { name: data.city.name, url: `/locations/${city}` },
            { name: data.h1, url: `/locations/${city}/${topic}` },
          ]),
          buildLocalBusinessSchema({ city, state: data.city.state, name: data.city.name }),
          ...(rich?.faqs?.length ? [buildFaqSchema(rich.faqs.map((f) => [f.q, f.a]))] : []),
        ]}
      />
      <Navbar />
      {preview && rich && <p className="bg-amber-100 text-amber-900 text-xs text-center py-2 mt-20" data-testid="loc-preview-banner">Preview — enriched content {rich.published_at ? "is live" : "is not published yet"}.</p>}
      <section className="container-wide section-pad" data-testid="location-hero">
        <p className="font-script text-3xl text-[var(--gold)] mb-3">
          {data.city.name}, {data.city.state}
        </p>
        <h1 className="font-serif text-5xl md:text-6xl font-light leading-tight max-w-4xl">
          {data.h1}
        </h1>
        <p className="font-body text-lg md:text-xl text-black/60 max-w-2xl mt-6">{data.tag}</p>
        <Link to={data.cta_route} className="btn-gold mt-10 inline-block" data-testid="location-cta">
          {data.cta_label}
        </Link>
        {rich && <div className="grid lg:grid-cols-[1fr_400px] gap-10 items-start mt-12" data-testid="location-enriched">
          <p className="text-base md:text-lg whitespace-pre-line max-w-2xl" data-testid="location-intro">{rich.intro}</p>
          <LeadForm p={rich} />
        </div>}
      </section>
      {rich && <RichBody p={rich} />}

      <section className="container-wide pb-24">
        <h2 className="font-serif text-3xl mb-6">More in {data.city.name}</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {data.keywords.map((k) => (
            <div key={k} className="border border-black/10 rounded-xl p-5 bg-white/40">
              <p className="text-sm text-black/70 capitalize">{k}</p>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}

export function LocationsIndexPage() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/seo/locations`).then((r) => setData(r.data));
  }, []);
  if (!data) return null;
  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <Seo
        title="Wedding Services by City"
        description="Find vetted wedding websites, planners, photographers, venues and DJs across 50 US wedding markets."
        path="/locations"
      />
      <Navbar />
      <section className="container-wide section-pad" data-testid="locations-index">
        <h1 className="font-serif text-5xl mb-3">Wedding services by city</h1>
        <p className="text-black/60 max-w-2xl mb-10">
          The top wedding markets in the country — explore vendors, planners, and venues curated by real couples.
        </p>
        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
          {data.cities.map((c) => (
            <div
              key={c.city}
              className="border border-black/10 rounded-xl p-5 bg-white/40"
              data-testid={`locations-card-${c.city}`}
            >
              <p className="font-serif text-2xl">{c.name}</p>
              <p className="text-xs uppercase tracking-widest text-black/50 mt-1">{c.state}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {data.topics.slice(0, 4).map((t) => (
                  <Link
                    key={t.slug}
                    to={`/locations/${c.city}/${t.slug}`}
                    className="text-xs underline text-[var(--gold)]"
                  >
                    {t.label}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  );
}
